import axios from "axios"
import type React from "react"
import { useEffect, useState } from "react"
import { toast, ToastContainer } from "react-toastify"
import "../../css/managed-postal-codes.css"
import SelectAllToggle from "./SelectAllToggle"
import LoaderAnnonce from "../../components/LoaderAnnonce"
import { FaCity } from "react-icons/fa"

const API_URL = process.env.REACT_APP_API_URL

const ManagedPostalCodes = () => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [postalCode, setPostalCode] = useState<string | null>(null)
  const [postalCodes, setPostalCodes] = useState<string[]>([]) // Stored postal codes in DB
  const [codeCommuneInitials, setCodeCommuneInitials] = useState<string[]>([]) // All available initials from JSON
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false)

  const handleCloseModal = () => {
    setModalIsOpen(false)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setPostalCode(value ? value : null)
  }

  const fetchPostalCodes = async () => {
    try {
      const response = await fetch(`${API_URL}/managed-postalcodes`)
      const data = await response.json()
      if (Array.isArray(data)) {
        const codes = data.map((item) => item.codeCommuneInitial)
        setPostalCodes(codes)
        // Check if all codes are selected
        setIsAllSelected(codes.length === codeCommuneInitials.length && codeCommuneInitials.length > 0)
      } else {
        setPostalCodes([])
        setIsAllSelected(false)
      }
    } catch (error) {
      toast.error("Erreur lors de la récupération des codes postaux.")
      setPostalCodes([])
      setIsAllSelected(false)
    }
  }

  const fetchCodeCommuneInitials = async () => {
    try {
      const response = await fetch(`${API_URL}/managed-postalcodes/code-commune-initials`)
      const data = await response.json()
      if (Array.isArray(data)) {
        setCodeCommuneInitials(data)
      } else {
        setCodeCommuneInitials([])
      }
    } catch (error) {
      toast.error("Erreur lors de la lecture des initiales.")
      setCodeCommuneInitials([])
    }
  }

  useEffect(() => {
    fetchPostalCodes()
    fetchCodeCommuneInitials()
  }, [])

  const handleAddRemovePostalCode = async (code: string, isChecked: boolean) => {
    try {
      setLoading(true)
      const url = `${API_URL}/managed-postalcodes/`
      let response
      if (isChecked) {
        response = await axios.post(url, { postalCode: code })
      } else {
        response = await axios.delete(url + code)
      }

      console.log("handleAddRemovePostalCode response", response)
      if (response.status === 200 || response.status === 201) {
        toast.success(isChecked ? "Code postal ajouté avec succès." : "Code postal supprimé avec succès.")
        await fetchPostalCodes()
      } else {
        toast.error("Erreur lors de la mise à jour du code postal.")
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout/suppression du code postal.")
    } finally {
      setLoading(false)
    }
  }

  const handleToggleAll = async () => {
    try {
      setLoading(true)
      const newState = !isAllSelected
      setIsAllSelected(newState)

      // If selecting all
      if (newState) {
        // Get codes that are not already selected
        const codesToAdd = codeCommuneInitials.filter((code) => !postalCodes.includes(code))

        if (codesToAdd.length > 0) {
          // Add all codes in parallel
          await Promise.all(
            codesToAdd.map((code) => axios.post(`${API_URL}/managed-postalcodes/`, { postalCode: code })),
          )
          toast.success("Tous les codes postaux ont été sélectionnés.")
        }
      }
      // If deselecting all
      else {
        // Remove all codes in parallel
        if (postalCodes.length > 0) {
          await Promise.all(postalCodes.map((code) => axios.delete(`${API_URL}/managed-postalcodes/${code}`)))
          toast.success("Tous les codes postaux ont été désélectionnés.")
        }
      }

      // Refresh the list
      await fetchPostalCodes()
    } catch (error) {
      toast.error("Erreur lors de la mise à jour des codes postaux.")
      setIsAllSelected(!isAllSelected)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <ToastContainer></ToastContainer>
      <div className="top">
        <div className="table-responsive" id="right_panel">
          <div className="table-wrapper">
            <div className="table-title">
              <div className="d-flex flex-row">
                <div className="d-flex flex-column py-3 ms-3">
                  <h2 style={{ color: "#2a919d", margin: 0, fontSize: "24px", marginBottom: "5px" }}>
                    Gestion des Codes Postaux
                  </h2>
                  <small style={{ color: "#2a919d" }}>
                    Les codes communes sélectionnés ci-dessous auront une attestation provenant du site alo-annonce-legal.
                  </small>
                </div>

              </div>
            </div>

            {/* Select All Toggle Button */}
            <div className="select-all-container">
              <h3>Sélectionner tous les codes postaux</h3>
              <SelectAllToggle isAllSelected={isAllSelected} onToggle={handleToggleAll} />
            </div>

            {/* Postal Code Grid */}
            {loading ? (
              <LoaderAnnonce></LoaderAnnonce>
            ) : (
              <div className="postal-code-grid">
                {codeCommuneInitials.length > 0 ? (
                  codeCommuneInitials.map((initial, index) => (
                    <div key={index} className="postal-code-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={postalCodes.includes(initial)}
                          onChange={(e) => handleAddRemovePostalCode(initial, e.target.checked)}
                        />
                        {initial}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="no-items">Aucune initiale trouvée</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ManagedPostalCodes